import * as React from 'react';

import { Button, TextField, cn } from '@rematter/ui-web';
import { Plus, Trash2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

type KeyValuePair = {
  key: string;
  value: string;
};

type KeyValueEditorProps = {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  label?: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  description?: string;
  className?: string;
};

/**
 * Key-value pair editor for headers, params, etc.
 */
export const KeyValueEditor: React.FC<KeyValueEditorProps> = ({
  value,
  onChange,
  label,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  description,
  className,
}) => {
  const t = useTranslations();
  const [pairs, setPairs] = React.useState<KeyValuePair[]>([]);

  // Convert object to array on mount and when value changes
  React.useEffect(() => {
    const newPairs = Object.entries(value).map(([key, val]) => ({
      key,
      value: val,
    }));
    // Always have at least one empty row
    if (newPairs.length === 0) {
      newPairs.push({ key: '', value: '' });
    }
    setPairs(newPairs);
  }, [value]);

  const handleUpdate = React.useCallback(
    (updatedPairs: KeyValuePair[]) => {
      // Convert array back to object, filtering out empty keys
      const newValue = updatedPairs.reduce(
        (acc, pair) => {
          if (pair.key.trim()) {
            acc[pair.key] = pair.value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange(newValue);
    },
    [onChange],
  );

  const handleKeyChange = React.useCallback(
    (index: number, newKey: string) => {
      const newPairs = [...pairs];
      newPairs[index].key = newKey;
      setPairs(newPairs);
      handleUpdate(newPairs);
    },
    [pairs, handleUpdate],
  );

  const handleValueChange = React.useCallback(
    (index: number, newValue: string) => {
      const newPairs = [...pairs];
      newPairs[index].value = newValue;
      setPairs(newPairs);
      handleUpdate(newPairs);
    },
    [pairs, handleUpdate],
  );

  const handleAdd = React.useCallback(() => {
    const newPairs = [...pairs, { key: '', value: '' }];
    setPairs(newPairs);
  }, [pairs]);

  const handleRemove = React.useCallback(
    (index: number) => {
      const newPairs = pairs.filter((_, i) => i !== index);
      // Ensure at least one empty row
      if (newPairs.length === 0) {
        newPairs.push({ key: '', value: '' });
      }
      setPairs(newPairs);
      handleUpdate(newPairs);
    },
    [pairs, handleUpdate],
  );

  return (
    <div className={cn('@KeyValueEditor', className)}>
      {label && (
        <label className="mb-2 block text-sm font-medium">{label}</label>
      )}
      {description && (
        <p className="mb-2 text-xs text-neutral/60">{description}</p>
      )}
      <div className="space-y-2">
        {pairs.map((pair, index) => (
          <div key={index} className="flex items-start gap-2">
            <TextField
              value={pair.key}
              onChange={(newKey) => handleKeyChange(index, newKey)}
              placeholder={keyPlaceholder}
              className="flex-1"
            />
            <TextField
              value={pair.value}
              onChange={(newValue) => handleValueChange(index, newValue)}
              placeholder={valuePlaceholder}
              className="flex-1"
            />
            <Button
              variant="ghost"
              size="small"
              icon={<Trash2 className="size-4" />}
              onPress={() => handleRemove(index)}
              isDisabled={pairs.length === 1}
              aria-label={t.editor.actions.remove}
            />
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        size="small"
        icon={<Plus className="size-4" />}
        onPress={handleAdd}
        className="mt-2"
      >
        {t.editor.actions.add}
      </Button>
    </div>
  );
};
